import type { ReactNode } from 'react'
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react'
import { useOrgContext } from '@/shared/ui/hooks'
import type { LegalDocument } from '@/shared/domain'
import type { RecordLegalAcceptance } from '@/modules/legal/use-cases'
import { writeCachedVersions } from '@/modules/legal/infra/cache'
import type { AuditMetadata } from '@/shared/infra/audit'
import { collectAuditMetadata } from '@/shared/infra/audit'

const LegalAcceptanceModal = lazy(() =>
  import('@/shared/ui/components/legal-acceptance-modal').then((m) => ({
    default: m.LegalAcceptanceModal,
  })),
)

type LegalProviderProps = {
  onRecordLegalAcceptance: RecordLegalAcceptance
  children: ReactNode
}

export function LegalProvider({ onRecordLegalAcceptance, children }: LegalProviderProps) {
  const { pendingLegalDocuments, refresh } = useOrgContext()
  const auditMetadataRef = useRef<AuditMetadata>({})

  useEffect(() => {
    let cancelled = false
    collectAuditMetadata()
      .then((metadata) => {
        if (cancelled) return
        auditMetadataRef.current = metadata
      })
      .catch(() => {
        if (cancelled) return
        auditMetadataRef.current = {}
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleAccept = useCallback(
    async (documents: LegalDocument[]) => {
      const metadata = auditMetadataRef.current
      await Promise.all(
        documents.map((document) =>
          onRecordLegalAcceptance({
            documentId: document.documentId,
            acceptanceMethod: 'modal',
            userAgent: metadata.userAgent,
            ipAddress: metadata.ipAddress,
          }),
        ),
      )
      const versionsByType = documents.reduce<Record<string, string>>((acc, document) => {
        acc[document.documentType] = document.version
        return acc
      }, {})
      writeCachedVersions(versionsByType)
      await refresh?.()
    },
    [onRecordLegalAcceptance, refresh],
  )

  const isOpen = pendingLegalDocuments.length > 0

  return (
    <>
      {children}
      {isOpen ? (
        <Suspense fallback={null}>
          <LegalAcceptanceModal
            documents={pendingLegalDocuments}
            onAccept={handleAccept}
            isOpen={isOpen}
          />
        </Suspense>
      ) : null}
    </>
  )
}
